/**
 * Turning a point on the page into a node of the document.
 *
 * This is the one place the two halves of the rendering layer meet: {@link geometry} knows
 * where things were drawn, {@link ids} knows what a drawn thing is called, and a click needs
 * both. Keeping it separate from either means neither has to know about the other.
 */

import type { EventId } from "../document/ids";
import { boxAt, boxNear, unionOf } from "./geometry";
import type { Box, Point } from "./geometry";
import { elementEventId, elementPitchIndex } from "./ids";
import type { Rendering } from "./render";

/** What a point landed on. */
export interface Hit {
  readonly event: EventId;
  /** Which note of a chord was hit; `0` for a single note or a rest. */
  readonly pitchIndex: number;
  /** The drawn element itself, for a caller that wants to style exactly what was clicked. */
  readonly elementId: string;
}

/**
 * The event under a point, or the nearest one within `radius`.
 *
 * A note head is a few pixels across and a pointer is not precise, so an exact miss falls
 * back to the nearest head. The radius is the caller's to choose because it depends on the
 * zoom: at half size, twice as many pixels are worth forgiving.
 */
export function hitAt(boxes: ReadonlyMap<string, Box>, point: Point, radius = 0): Hit | null {
  const direct = boxAt(boxes, point);
  const elementId = direct ?? (radius > 0 ? boxNear(boxes, point, radius) : null);
  if (elementId === null) return null;
  const event = elementEventId(elementId);
  if (event === null) return null;
  return { event, pitchIndex: elementPitchIndex(elementId), elementId };
}

/**
 * The area an event occupies, which for a chord is all of its note heads together.
 *
 * Returns `null` when none of the event's elements were measured -- it is on another page,
 * or the engine did not draw it -- so a caller can tell "nothing to outline" from "an
 * outline of nothing", which a zero-sized box would not.
 */
export function eventBox(
  rendering: Rendering,
  boxes: ReadonlyMap<string, Box>,
  id: EventId,
): Box | null {
  const drawn = rendering.eventElements.get(id) ?? [];
  const found: Box[] = [];
  for (const elementId of drawn) {
    const box = boxes.get(elementId);
    if (box !== undefined) found.push(box);
  }
  return unionOf(found);
}

/** Every drawn element of every event of a rendering, for measuring a page in one pass. */
export function elementsOnPage(rendering: Rendering, page: number): readonly string[] {
  const ids: string[] = [];
  for (const [event, drawn] of rendering.eventElements) {
    if (rendering.eventPage.get(event) !== page) continue;
    ids.push(...drawn);
  }
  return ids;
}
